import React, { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { madness, type GlassCrackMark } from '../utils/madness';
import { CRACK_TEXTURES, buildShards, seededRandom } from '../utils/glassCrack';
const EASE_OUT = [0.23, 1, 0.32, 1] as const;
/** Broken glass stays this long, then fades away (ms) */
const CRACK_LIFE = 13000;
const FADE_S = 1.4;

/**
 * Broken screen left behind by Pixel's crash (madness stage 2): a photographic, transparent
 * crack overlay centred on the impact. Fixed over the whole site, never blocks clicks,
 * and fades away after ~13s.
 */
export function GlassCrackOverlay() {
  const cracks = useSyncExternalStore(madness.subscribe, madness.getCracks, madness.getCracks);
  const [size, setSize] = useState(() => ({
    w: window.innerWidth,
    h: window.innerHeight
  }));
  useEffect(() => {
    const onResize = () => setSize({
      w: window.innerWidth,
      h: window.innerHeight
    });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // No wrapper element: each layer must sit directly in the page so its blend mode sees the page behind it
  return <>
      {cracks.map(c => <Crack key={c.seed} mark={c} w={size.w} h={size.h} data-id="element-69" data-mp="1gwan6u4b6ffw" />)}
    </>;
}
function Crack({
  mark,
  w,
  h
}: {
  mark: GlassCrackMark;
  w: number;
  h: number;
}) {
  const reduce = useReducedMotion();
  const shards = useMemo(() => buildShards(mark.seed), [mark.seed]);
  const look = useMemo(() => {
    const rnd = seededRandom(mark.seed + 11);
    return {
      src: CRACK_TEXTURES[Math.floor(rnd() * CRACK_TEXTURES.length)],
      rotate: rnd() * 360,
      flip: rnd() < 0.5 ? -1 : 1
    };
  }, [mark.seed]);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setFading(true), CRACK_LIFE);
    return () => window.clearTimeout(id);
  }, []);

  // Texture is square and centred on the impact; big enough that cracks run off the screen edges
  const size = Math.max(w, h) * 1.7;
  const reach = Math.ceil(Math.hypot(Math.max(mark.x, w - mark.x), Math.max(mark.y, h - mark.y)) + 40);
  const texture: React.CSSProperties = {
    position: 'absolute',
    left: mark.x - size / 2,
    top: mark.y - size / 2,
    width: size,
    height: size,
    maxWidth: 'none',
    transform: `rotate(${look.rotate}deg) scaleX(${look.flip})`,
    // Soft radial falloff hides the photo's edges, so it reads as one cracked pane
    maskImage: 'radial-gradient(circle at center, black 0%, black 30%, transparent 47%)',
    WebkitMaskImage: 'radial-gradient(circle at center, black 0%, black 30%, transparent 47%)'
  };

  // Layer fade-out (13s) and the crack racing outward from the impact, with a short jolt
  const layer = (opacity: number) => ({
    initial: {
      opacity
    },
    animate: {
      opacity: fading ? 0 : opacity
    },
    transition: {
      duration: fading ? FADE_S : 0,
      ease: [0.4, 0, 0.2, 1]
    }
  });
  const spread = {
    initial: reduce ? false : {
      clipPath: `circle(0px at ${mark.x}px ${mark.y}px)`
    },
    animate: {
      clipPath: `circle(${reach}px at ${mark.x}px ${mark.y}px)`,
      ...(reduce ? {} : {
        x: [0, -9, 7, -4, 2, 0],
        y: [0, 5, -4, 2, -1, 0]
      })
    },
    transition: {
      duration: reduce ? 0 : 0.28,
      ease: [0.16, 1, 0.3, 1]
    }
  } as const;
  return <>
      {/* Dark fractures — multiply drops the photo's white, leaving clear glass with cracks */}
      <motion.div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[70] overflow-hidden mix-blend-multiply" {...layer(0.85)} onAnimationComplete={() => {
      if (fading) madness.removeCrack(mark.seed);
    }} data-id="element-70" data-mp="2f77jq510knh5">
        <motion.div className="absolute inset-0" {...spread} data-id="element-71" data-mp="268nbxywzry7p">
          <img src={look.src} alt="" draggable={false} style={{
          ...texture,
          filter: 'grayscale(1) brightness(1.1) contrast(1.35)'
        }} data-id="element-72" data-mp="3vmsez7u0003b" />
        </motion.div>
      </motion.div>

      {/* Bright edges — inverted + screen, so the cracks also catch the light over dark areas like the video */}
      <motion.div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[71] overflow-hidden mix-blend-screen" {...layer(0.5)} data-id="element-73" data-mp="1viy5rh9szqg6">
        <motion.div className="absolute inset-0" {...spread} data-id="element-74" data-mp="1mitcsc5cwulj">
          <img src={look.src} alt="" draggable={false} style={{
          ...texture,
          translate: '-1px -1px',
          filter: 'grayscale(1) invert(1) brightness(0.95) contrast(1.6)'
        }} data-id="element-75" data-mp="1mi9wt0z9oga5" />
        </motion.div>
      </motion.div>

      {/* Impact flash + glass fragments bursting out and falling away */}
      {!reduce && <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[72] overflow-hidden" data-id="element-76" data-mp="12vllxev11wqk">
          <motion.div className="absolute inset-0 bg-white" initial={{
        opacity: 0.55
      }} animate={{
        opacity: 0
      }} transition={{
        duration: 0.28,
        ease: EASE_OUT
      }} data-id="element-77" data-mp="yrxf4ye7z56d" />
          <svg className="absolute inset-0 h-full w-full overflow-visible" viewBox={`0 0 ${w} ${h}`} data-id="element-78" data-mp="35gpk1od9n5ao">
            {shards.map((s, i) => <g key={i} transform={`translate(${mark.x} ${mark.y})`} data-id="element-79" data-mp="3ehw3a57auu7m" data-mpi={i}>
                <motion.polygon points={s.points} fill="rgba(235,242,248,0.6)" stroke="rgba(18,20,24,0.4)" strokeWidth={0.7} style={{
            transformBox: 'fill-box',
            transformOrigin: 'center'
          }} initial={{
            x: 0,
            y: 0,
            rotate: 0,
            opacity: 1
          }} animate={{
            x: s.dx,
            y: [0, s.dy * 0.25, s.dy],
            rotate: s.rotate,
            opacity: [1, 1, 0]
          }} transition={{
            duration: 0.75,
            delay: s.delay,
            ease: [0.2, 0.6, 0.35, 1]
          }} data-id="element-80" data-mp="3eft6n2bn62tx" />
              </g>)}
          </svg>
        </div>}
    </>;
}